import React from "react";
import { MdExpandMore } from "react-icons/md";
import { HiOutlineChevronDoubleUp } from "react-icons/hi";
import { Tooltip } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import EAButton from "../../shared/Buttons/EAButton";
import EAIconButton from "../../shared/Buttons/EAIconButton";

interface ListExpandControlsProps {
  displayedCount: number;
  totalCount: number;
  setDisplayedCount: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  showMoreLabelKey: string;
  onCollapseAllAndScrollTop: () => void;
}

const ListExpandControls: React.FC<ListExpandControlsProps> = ({
  displayedCount,
  totalCount,
  setDisplayedCount,
  itemsPerPage,
  showMoreLabelKey,
  onCollapseAllAndScrollTop,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 980px)");

  const hasExpanded = displayedCount > itemsPerPage;
  const itemsToAdd = Math.min(itemsPerPage, totalCount - displayedCount);
  const newCountAfterCollapse = Math.max(itemsPerPage, displayedCount - itemsPerPage);
  const itemsToCollapse = displayedCount - newCountAfterCollapse;

  const handleShowMore = () => {
    setDisplayedCount((prev) => Math.min(prev + itemsPerPage, totalCount));
  };

  const handleShowLess = () => {
    setDisplayedCount(newCountAfterCollapse);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        justifyContent: "center",
        marginTop: "16px",
      }}
    >
      {hasExpanded && (
        <>
          <EAIconButton
            icon={<HiOutlineChevronDoubleUp />}
            title={t("ukryj_wszystko_i_na_gore")}
            variant="outlined"
            onClick={onCollapseAllAndScrollTop}
            compact
          />
          {isMobile ? (
            <EAIconButton
              icon={<MdExpandMore style={{ transform: "rotate(180deg)" }} />}
              title={t("pokaz_mniej_tooltip", { count: itemsToCollapse })}
              variant="outlined"
              onClick={handleShowLess}
              compact
            />
          ) : (
            <Tooltip title={t("pokaz_mniej_tooltip", { count: itemsToCollapse })}>
              <span>
                <EAButton
                  title={`${t("pokaz_mniej")} (-${itemsToCollapse})`}
                  variant="outlined"
                  endIcon={<MdExpandMore style={{ transform: "rotate(180deg)" }} />}
                  onClick={handleShowLess}
                  compact
                />
              </span>
            </Tooltip>
          )}
        </>
      )}
      <span className="caption text-color-dark-gray">
        {displayedCount} z {totalCount}
      </span>
      {isMobile ? (
        <EAIconButton
          icon={<MdExpandMore />}
          title={
            itemsToAdd > 0
              ? t("pokaz_wiecej_tooltip", { count: itemsToAdd })
              : t("brak_wiecej_elementow")
          }
          variant="outlined"
          onClick={handleShowMore}
          disabled={itemsToAdd === 0}
          compact
        />
      ) : (
        <Tooltip
          title={
            itemsToAdd > 0
              ? t("pokaz_wiecej_tooltip", { count: itemsToAdd })
              : t("brak_wiecej_elementow")
          }
        >
          <span>
            <EAButton
              title={`${t(showMoreLabelKey)} (+${itemsToAdd})`}
              variant="outlined"
              endIcon={<MdExpandMore />}
              onClick={handleShowMore}
              disabled={itemsToAdd === 0}
              compact
            />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default ListExpandControls;
